import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { RecentJourneysScreen } from '~/screens/Home/RecentJourneysScreen';
import { NewInspectionScreen } from '~/screens/Inspection/NewInspectionScreen';
import { JobsScreen } from '~/screens/Jobs/JobsScreen';
import { useTheme } from '~/theme/ThemeProvider';

/**
 * Linear stack, not a tab bar.
 *
 * The inspection flow is a sequence holding real state — a scanned VIN, selected areas,
 * captured photos — and a tab bar invites walking away from a half-built one. That is
 * not hypothetical: the reset-on-blur logic in NewInspectionScreen exists ONLY because
 * tabs made it possible to leave mid-capture and return to a stale journey. A stack
 * makes that class of bug impossible rather than handled.
 *
 * Headers are off everywhere. Each screen draws its own, because they carry a step
 * counter, progress pips and a claim reference — none of which a stock header expresses.
 */
export type RootStackParamList = {
  Home: undefined;
  Inspect: undefined;
  Jobs: undefined;
};

const Stack = createNativeStackNavigator<RootStackParamList>();

export function AuthenticatedShell() {
  const { c } = useTheme();
  return (
    <NavigationContainer
      theme={{
        dark: true,
        colors: {
          primary: c.primary,
          background: c.surface,
          card: c.s2,
          text: c.text,
          border: c.border,
          notification: c.primary,
        },
        fonts: {
          regular: { fontFamily: 'InstrumentSans', fontWeight: '400' },
          medium: { fontFamily: 'InstrumentSans', fontWeight: '500' },
          bold: { fontFamily: 'InstrumentSans', fontWeight: '600' },
          heavy: { fontFamily: 'InstrumentSans', fontWeight: '700' },
        },
      }}
    >
      <Stack.Navigator
        initialRouteName="Home"
        screenOptions={{
          headerShown: false,
          contentStyle: { backgroundColor: c.surface },
          // Capture screens hold an open camera, and a swipe-back mid-capture would
          // tear the session down at an awkward moment. Back is an explicit control.
          gestureEnabled: false,
        }}
      >
        <Stack.Screen name="Home" component={RecentJourneysScreen} />
        <Stack.Screen name="Inspect" component={NewInspectionScreen} />
        <Stack.Screen name="Jobs" component={JobsScreen} />
      </Stack.Navigator>
    </NavigationContainer>
  );
}
